import * as ents from '@attain-apps/entities'
import { TypeAsserter } from '@attain-apps/type-guards'
import type cytoscape from 'cytoscape'

import type { Feature } from '../../feature'
import * as models from '../../models'

/**
 * Applies appropriate classes to style each node appropriately
 */
export function setupNodeClasses(
  cy: cytoscape.Core,
  params: setupNodeClasses.AdditionalParams,
): void {
  refreshNodeClasses(cy, params)

  // For new nodes, apply classes
  cy.on('add', 'node', event => {
    const newNode = event.target as cytoscape.NodeSingular
    applyAppropriateClasses(newNode, params)
  })
}
export namespace setupNodeClasses {
  export interface AdditionalParams extends models.Chart.Identifier {
    readonly feature: Feature
  }
}

/**
 * Re-applies derived classes for every node after proposal state mutations.
 */
export function refreshNodeClasses(
  cy: cytoscape.Core,
  params: setupNodeClasses.AdditionalParams,
): void {
  cy.nodes().forEach(node => applyAppropriateClasses(node, params))
}

/**
 * Used by {@link setupNodeClasses} to apply the appropriate classes to a
 * specific node based on the attributes of that node
 */
function applyAppropriateClasses(
  node: cytoscape.NodeSingular,
  { feature, chartId }: setupNodeClasses.AdditionalParams,
): void {
  // Type class
  const typeClass = (node.data('type') as unknown)?.toString()
  if (typeClass) {
    node.addClass(typeClass)
  }

  if (models.Chart.Entry.get(chartId) !== 'proposed') {
    node.removeClass('changed')
    return
  }

  const hasChange = feature.selectors
    .hasProposedChange({
      chartId,
      nodeId: TypeAsserter.validate(
        node.id(),
        ents.core.display.tree.Node.Id.assertIsId,
      ),
    })
    .asSignal()

  if (hasChange) {
    node.addClass('changed')
  } else {
    node.removeClass('changed')
  }
}
